/* eslint-disable no-nested-ternary */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Autocomplete,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  Filter,
  KeyValuePair,
  PaginationKeyValuePair,
} from '@utils/buckets/type'
import { ScheduleFilterSelectContext } from './ScheduleFilterSelectContext'
import { useContextSelector } from 'use-context-selector'
import { useEffect, useState } from 'react'

export function ScheduleFilterSelectOptions(props: {
  options?: KeyValuePair[]
  filter: Filter
  active: boolean
}): JSX.Element {
  const { options, filter, active } = props

  const selectOption = useContextSelector(
    ScheduleFilterSelectContext,
    (s) => s.handleChangeSelectOption
  )

  const selecteds = useContextSelector(
    ScheduleFilterSelectContext,
    (s) => s.selecteds
  )

  const [subfilterOptions, setSubfilterOptions] = useState<Set<KeyValuePair>>(
    new Set<KeyValuePair>()
  )

  const variationSwitch = filter.type && filter.type === 'switch'
  const disabled =
    filter.upperRef !== undefined &&
    (selecteds.get(filter.upperRef) === undefined ||
      selecteds.get(filter.upperRef) === null ||
      selecteds.get(filter.upperRef) === '')

  const manageSubfilter = async () => {
    if (disabled || variationSwitch || !filter.options) return
    const localOptions = new Set<KeyValuePair>(
      filter.upperRef ? [] : subfilterOptions
    )
    let params = {
      text: '',
      limit: 1000,
      page: 1,
    }

    if (filter.attr.startsWith('eagenda')) {
      params = {
        organ: selecteds.get('eagenda_organ'),
        cargo: selecteds.get('eagenda_cargo'),
        stakeholder: selecteds.get('eagenda_stakeholder'),
      } as never
      const result = (await filter.options(params)) as unknown as {
        organs: string[]
        cargos: string[]
        stakeholders: string[]
      }
      if (filter.attr === 'eagenda_organ') {
        result.organs.forEach((r) => {
          const index = Array.from(localOptions).find(
            (v) => v.key === r && v.value === r
          )
          if (!index)
            localOptions.add({
              key: r,
              value: r,
            })
        })
      } else if (filter.attr === 'eagenda_cargo') {
        result.cargos.forEach((r) => {
          localOptions.add({
            key: r,
            value: r,
          })
        })
      } else if (filter.attr === 'eagenda_stakeholder') {
        result.stakeholders.forEach((r) => {
          localOptions.add({
            key: r,
            value: r,
          })
        })
      }
    } else if (filter.attr === 'openDataResource') {
      const result = (await filter.options(params)) as KeyValuePair[]
      result.forEach((r) => {
        localOptions.add(r)
      })
    } else if (filter.attr.startsWith('panel')) {
      if (filter.attr === 'panel_monitors') {
        const result = (await filter.options(params)) as PaginationKeyValuePair
        result.results.forEach((r) => {
          const index = Array.from(localOptions).find(
            (v) => v.key === r.key && v.value === r.value
          )
          if (!index) localOptions.add(r)
        })
      } else if (filter.attr === 'panel_saved_searches') {
        params = {
          monitorId: selecteds.get('panel_monitors'),
        } as never
        const result = (await filter.options(params)) as {
          id: string
          name: string
          keywords: never
        }[]
        result.forEach((r) => {
          const index = Array.from(localOptions).find(
            (v) => v.key === r.id && v.value === r.name
          )
          if (!index)
            localOptions.add({
              key: r.id,
              value: r.name,
              keywords: r.keywords,
            })
        })
      } else if (filter.attr === 'panel_stakeholders') {
        params = {
          limit: 1000,
          page: 1,
          monitor: selecteds.get('panel_monitors'),
        } as never
        const result = (await filter.options(params)) as {
          results: {
            id: string
            name: string
          }[]
        }
        result.results.forEach((r) => {
          const index = Array.from(localOptions).find(
            (v) => v.key === r.id && v.value === r.name
          )
          if (!index)
            localOptions.add({
              key: r.id,
              value: r.name,
            })
        })
      }
    }
    setSubfilterOptions(localOptions)
  }

  useEffect(() => {
    manageSubfilter()
  }, [selecteds])

  return (
    <>
      {variationSwitch && (
        <Stack direction="row" spacing={2}>
          <Radio
            size="small"
            disabled={disabled}
            checked={selecteds.get(filter.attr) === 'true'}
            onClick={() => {
              const checked = selecteds.get(filter.attr) !== 'true'
              selectOption([{ filter: filter.attr, key: String(checked) }])
            }}
            sx={{
              color: active ? '#202122' : '#B6BCC9',
              '&.MuiButtonBase-root': {
                padding: 0,
                marginRight: '4px',
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '12px',
              color: '$color-text-primary',
              fontWeight: 600,
            }}
            data-testid="title"
            variant="$font-body-5"
          >
            {filter.label}
          </Typography>
        </Stack>
      )}
      {!variationSwitch && (
        <Stack direction="column" spacing={2}>
          <Typography
            sx={{
              fontSize: '12px',
              color: '$color-text-primary',
              fontWeight: 600,
            }}
            data-testid="title"
            variant="$font-body-5"
          >
            {filter.label}
          </Typography>
          <Autocomplete
            size="small"
            noOptionsText="Opção não disponível"
            disablePortal
            disabled={disabled}
            popupIcon={<ExpandMoreIcon />}
            options={options || Array.from(subfilterOptions)}
            getOptionLabel={(option) => option.value}
            renderOption={(props, option) => (
              <li {...props} key={option.key}>
                {option.value}
              </li>
            )}
            isOptionEqualToValue={(option, value) => {
              return option.key === value.key && option.value === value.value
            }}
            renderInput={(params) => {
              return <TextField {...params} placeholder="Selecionar" />
            }}
            onChange={(_, value) => {
              const params = new Array<unknown>()
              params.push({
                filter: filter.attr,
                key: value ? (value.keywords ? value.keywords : value.key) : '',
              })
              if (filter.attr === 'eagenda_organ') {
                params.push({ filter: 'eagenda_cargo', key: '' })
                params.push({ filter: 'eagenda_stakeholder', key: '' })
              } else if (filter.attr === 'panel_monitors') {
                params.push({ filter: 'panel_saved_searches', key: '' })
                params.push({ filter: 'panel_stakeholders', key: '' })
                params.push({ filter: 'panel_propositions', key: '' })
              }
              selectOption(params as never)
            }}
          />
        </Stack>
      )}
    </>
  )
}
